/* Copyright (c) 2020-present CloudIO Inc */

import { checkForFixedChild } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './AutoSizer';
const name = 'Auto Sizer';
const AutoSizerManager: ComponentManagerFn<'AutoSizer'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AutoSizer'>(platform, 'AutoSizer'),
    type: 'AutoSizer',
    name,
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          'CanvasGrid',
          'Card',
          'Chartjs',
          'CombinationChart',
          'D3Chart',
          'DataGrid',
          'DataStoreReference',
          'FlexPanel',
          'HorizontalSplitPanel',
          'TabPanel',
          'VerticalSplitPanel',
          'ViewAttachment',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    defaultValue,
    props: {},
    category: 'Layout',
    icon: { iconName: 'expand-alt' },
    description: `**Overview**<br/>
The Auto Sizer is a specialized Layout utility component designed to bridge the gap between "Auto Layout" containers and "Fixed Layout" components. Its primary function is to wrap components that require explicit dimensions (height/width) so they can render correctly inside containers that do not provide them by default.
Use this component when you need to place a complex visualization (like a Chart or Data Grid) inside a flexible container (like a Box) that would otherwise cause the visualization to collapse or not render.

**1. The Problem it Solves**<br/>
In modern web layouts, some containers grow based on their content (Auto Layout), while complex components often need to know exactly how much space they have to draw themselves (Fixed Layout).
* **Conflict:** If you put a **Chartjs** (which needs height) directly into a simple **Box** (which hugs content), the chart might render with 0 height because the box is waiting for the chart to size itself, and the chart is waiting for the box to give it space.
* **Solution:** The Auto Sizer detects the available space in the parent container and passes those exact pixel dimensions down to its child, ensuring the child renders at full size and remains responsive.

**2. Usage & Configuration**<br/>
* **Zero Configuration:** This component has no configurable properties (props: {}). It works purely as a structural, logic-based wrapper.
* **Constraint:** It is designed to host exactly **one** child component at a time. This ensures the sizing logic remains deterministic and the single child can occupy the entire available area.

**3. Allowed Children**<br/>
The Auto Sizer is strict about its contents, supporting specific complex components that require dimension-awareness:
* **Visualizations:** Chartjs, CombinationChart, and D3Chart.
* **Data Views:** DataGrid and Card components.
* **Layouts:** CanvasGrid, FlexPanel, HorizontalSplitPanel, VerticalSplitPanel, and TabPanel.
* **Others:** ViewAttachment and DataStoreReference.

**4. Best Use Cases**<br/>
* **Charts in Flexboxes:** Placing a responsive bar chart inside a dynamic flex column without the chart collapsing.
* **Grids in Modals:** Rendering a large **DataGrid** inside a dynamic dialog box where the modal size might change based on screen resolution.
* **Split Screens:** Nesting split panels inside a layout that doesn't have a fixed height assigned at the root level.
* **Full-Page Dashboards:** Using the Auto Sizer as a top-level wrapper to ensure a complex dashboard fills the entire browser viewport.

**5. Technical Note**<br/>
* **Layout Type:** The component defines its layout as **'fixed'**, meaning it claims a specific spatial context and passes that context down to its descendant.
* **Validation:** The system prevents adding multiple children or incompatible component types to prevent rendering loops or layout breaks.`,
    layout: 'fixed',
  };
};

export default AutoSizerManager;
